<template>
  <div class="dns-records-management">
    <b-overlay
      :show="isLoading"
      rounded="sm"
      variant="secondary"
      opacity="0.75"
    >
      <!-- DNS Records List -->
      <div
        v-if="records.length > 0"
        class="mb-2"
      >
        <b-table
          :items="records"
          :fields="tableFields"
          responsive
          striped
          hover
          small
          class="mb-0"
        >
          <template #cell(name)="data">
            <code class="text-primary">{{ data.value }}</code>
          </template>

          <template #cell(content)="data">
            <code
              class="text-break"
              style="font-size: 0.85rem;"
            >{{ data.value }}</code>
          </template>

          <template #cell(type)="data">
            <b-badge
              variant="light-info"
              pill
            >
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div class="d-flex align-items-center">
              <feather-icon
                v-b-tooltip.hover.v-primary.viewport
                icon="EditIcon"
                size="16"
                class="cursor-pointer text-primary mr-50"
                :title="$t('Edit')"
                @click="editRecord(data.item)"
              />
              <feather-icon
                v-b-tooltip.hover.v-danger.viewport
                icon="Trash2Icon"
                size="16"
                class="cursor-pointer text-danger"
                :title="$t('Delete')"
                @click="deleteRecord(data.item.id)"
              />
            </div>
          </template>
        </b-table>
      </div>

      <!-- Empty State -->
      <b-alert
        v-else
        show
        variant="light"
        class="mb-2"
      >
        <div class="alert-body">
          <feather-icon
            icon="InfoIcon"
            class="mr-25"
          />
          <span>{{ $t('modules.settings.dns_records.no_records') }}</span>
        </div>
      </b-alert>

      <!-- Add/Edit Form -->
      <b-card
        no-body
        class="mb-0"
        :class="{'border-primary': isFormVisible}"
      >
        <b-card-header
          class="pb-1"
          style="cursor: pointer;"
          @click="isFormVisible = !isFormVisible"
        >
          <b-card-title class="mb-0">
            <feather-icon
              :icon="isFormVisible ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="mr-50"
            />
            {{ editingRecord ? $t('modules.settings.dns_records.edit_record') : $t('modules.settings.dns_records.add_record') }}
          </b-card-title>
        </b-card-header>

        <b-collapse v-model="isFormVisible">
          <b-card-body>
            <b-form @submit.prevent="submitRecord">
              <b-row>
                <!-- Record Type -->
                <b-col
                  cols="12"
                  md="3"
                >
                  <b-form-group
                    :label="$t('modules.settings.dns_records.fields.type')"
                    label-for="record-type"
                  >
                    <b-form-select
                      id="record-type"
                      v-model="formData.type"
                      :options="recordTypes"
                      disabled
                    />
                  </b-form-group>
                </b-col>

                <!-- Record Name -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group
                    :label="$t('modules.settings.dns_records.fields.name')"
                    label-for="record-name"
                    :state="validationState('name')"
                    :invalid-feedback="validationError('name')"
                  >
                    <b-form-input
                      id="record-name"
                      v-model="formData.name"
                      :state="validationState('name')"
                      :placeholder="$t('modules.settings.dns_records.placeholders.name')"
                      maxlength="255"
                      trim
                    />
                    <small class="text-muted">
                      {{ $t('modules.settings.dns_records.hints.name') }}
                    </small>
                  </b-form-group>
                </b-col>

                <!-- Record Content -->
                <b-col
                  cols="12"
                  md="5"
                >
                  <b-form-group
                    :label="$t('modules.settings.dns_records.fields.content')"
                    label-for="record-content"
                    :state="validationState('content')"
                    :invalid-feedback="validationError('content')"
                  >
                    <b-form-textarea
                      id="record-content"
                      v-model="formData.content"
                      :state="validationState('content')"
                      :placeholder="$t('modules.settings.dns_records.placeholders.content')"
                      rows="2"
                      max-rows="4"
                      maxlength="2048"
                      trim
                    />
                    <small class="text-muted">
                      {{ $t('modules.settings.dns_records.hints.content') }}
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Form Actions -->
              <b-row>
                <b-col cols="12">
                  <div class="d-flex justify-content-end">
                    <b-button
                      v-if="editingRecord"
                      variant="outline-secondary"
                      class="mr-50"
                      @click="cancelEdit"
                    >
                      {{ $t('Cancel') }}
                    </b-button>
                    <b-button
                      variant="primary"
                      type="submit"
                      :disabled="isLoading"
                    >
                      <feather-icon
                        :icon="editingRecord ? 'SaveIcon' : 'PlusIcon'"
                        size="16"
                        class="mr-25"
                      />
                      {{ editingRecord ? $t('Save') : $t('Add') }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-form>
          </b-card-body>
        </b-collapse>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BCardTitle,
  BTable,
  BBadge,
  BAlert,
  BOverlay,
  BCollapse,
  VBTooltip,
} from 'bootstrap-vue'
import { onMounted, onUnmounted, ref } from 'vue'
import { toast, catchException } from '@core/utils/utils'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import Ripple from 'vue-ripple-directive'
import DomainModel from '@/views/models/domains/domainModel'
import store from '@/store'
import domainStoreModule from '@/views/models/domains/domainStoreModule'
import useModelDelete, {
  DEFAULT_ALWAYS_CALLBACK,
  DEFAULT_ON_APPROVE_CALLBACK, DEFAULT_ON_FAILURE_CALLBACK,
  DEFAULT_ON_SUCCESS_CALLBACK,
} from '@/views/models/common/useModelDelete'

export default {
  name: 'DnsRecordsManagement',
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    BTable,
    BBadge,
    BAlert,
    BOverlay,
    BCollapse,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  props: {
    domainId: {
      type: [String, Number],
      required: true,
    },
    initialRecords: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const STORE_MODULE_NAME = DomainModel.name

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, domainStoreModule)
    onMounted(() => {
      if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, domainStoreModule)
    })

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const { t } = useI18nUtils()
    const isLoading = ref(false)
    const isFormVisible = ref(false)
    const records = ref([...props.initialRecords])
    const editingRecord = ref(null)
    const remoteErrors = ref({})

    const formData = ref({
      type: 'TXT',
      name: '',
      content: '',
    })

    const recordTypes = [
      { value: 'TXT', text: 'TXT' },
    ]

    const tableFields = [
      {
        key: 'type', label: t('modules.settings.dns_records.table.type'), thClass: 'text-center', tdClass: 'text-center',
      },
      { key: 'name', label: t('modules.settings.dns_records.table.name') },
      { key: 'content', label: t('modules.settings.dns_records.table.content') },
      {
        key: 'actions', label: t('Actions'), thClass: 'text-center', tdClass: 'text-center',
      },
    ]

    const resetForm = () => {
      formData.value = {
        type: 'TXT',
        name: '',
        content: '',
      }
      editingRecord.value = null
      remoteErrors.value = {}
    }

    const fetchRecords = async () => {
      isLoading.value = true
      try {
        const response = await store.dispatch(`${STORE_MODULE_NAME}/fetchDnsRecords`, props.domainId)
        records.value = response.data.data
      } catch (error) {
        catchException(error)
        toast(
          'danger',
          t('modules.settings.dns_records.messages.fetch_error'),
          null,
          'AlertTriangleIcon',
        )
      } finally {
        isLoading.value = false
      }
    }

    const submitRecord = async () => {
      isLoading.value = true
      remoteErrors.value = {}

      try {
        const payload = {
          domainId: props.domainId,
          name: formData.value.name,
          content: formData.value.content,
        }

        let response
        if (editingRecord.value) {
          // Update existing record
          payload.id = editingRecord.value.id
          response = await store.dispatch(`${STORE_MODULE_NAME}/updateDnsRecord`, payload)
          toast(
            'success',
            t('modules.settings.dns_records.messages.update_success'),
            null,
            'CheckIcon',
          )
        } else {
          // Create new record
          response = await store.dispatch(`${STORE_MODULE_NAME}/createDnsRecord`, payload)
          toast(
            'success',
            t('modules.settings.dns_records.messages.create_success'),
            null,
            'CheckIcon',
          )
        }

        // Refresh records list
        await fetchRecords()
        resetForm()
        isFormVisible.value = false
      } catch (error) {
        catchException(error)

        // Handle validation errors
        if (error.response?.data?.errors) {
          remoteErrors.value = error.response.data.errors
        } else if (error.response?.data?.error) {
          toast(
            'danger',
            error.response.data.error,
            null,
            'AlertTriangleIcon',
          )
        } else {
          toast(
            'danger',
            t('modules.settings.dns_records.messages.save_error'),
            null,
            'AlertTriangleIcon',
          )
        }
      } finally {
        isLoading.value = false
      }
    }

    const editRecord = record => {
      editingRecord.value = record
      formData.value = {
        type: record.type,
        name: record.name,
        content: record.content,
      }
      isFormVisible.value = true
      remoteErrors.value = {}
    }

    const cancelEdit = () => {
      resetForm()
      isFormVisible.value = false
    }

    const {
      deleteItems,
    } = useModelDelete(
      STORE_MODULE_NAME,
      false,
      () => {
        DEFAULT_ON_APPROVE_CALLBACK()
        isLoading.value = true
      },
      () => {
        DEFAULT_ON_SUCCESS_CALLBACK()
        fetchRecords()
      },
      () => {
        DEFAULT_ON_FAILURE_CALLBACK()
      },
      () => {
        DEFAULT_ALWAYS_CALLBACK()
        isLoading.value = false
      },
    )

    const deleteRecord = id => {
      deleteItems([`${props.domainId}/dns-records/${id}`])
    }

    const validationState = field => {
      if (remoteErrors.value[field]) {
        return false
      }
      return null
    }

    const validationError = field => remoteErrors.value[field]?.[0] || ''

    // Load records on mount
    fetchRecords()

    return {
      isLoading,
      isFormVisible,
      records,
      editingRecord,
      formData,
      recordTypes,
      tableFields,
      remoteErrors,
      submitRecord,
      editRecord,
      cancelEdit,
      deleteRecord,
      validationState,
      validationError,
      fetchRecords,
    }
  },
}
</script>

<style lang="scss" scoped>
.dns-records-management {
  .cursor-pointer {
    cursor: pointer;
  }

  .text-break {
    word-break: break-word;
  }
}
</style>
